import { useState, useCallback } from 'react';

/**
 * useConfirmDialog - Hook for managing confirmation dialogs
 *
 * Replaces native browser confirm() with styled modal dialogs
 *
 * @example
 * const { confirmDialog, showConfirm } = useConfirmDialog();
 *
 * const handleDelete = async () => {
 *   const confirmed = await showConfirm({
 *     title: "Delete Item",
 *     message: "Are you sure? This cannot be undone.",
 *     variant: "danger"
 *   });
 *
 *   if (confirmed) {
 *     // proceed with deletion
 *   }
 * };
 *
 * return (
 *   <>
 *     <button onClick={handleDelete}>Delete</button>
 *     {confirmDialog}
 *   </>
 * );
 */
export function useConfirmDialog() {
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'OK',
    cancelText: 'Cancel',
    variant: 'warning',
    resolver: null
  });

  const showConfirm = useCallback(({
    title,
    message,
    confirmText = 'OK',
    cancelText = 'Cancel',
    variant = 'warning'
  }) => {
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        title,
        message,
        confirmText,
        cancelText,
        variant,
        resolver: resolve
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (dialogState.resolver) {
      dialogState.resolver(true);
    }
    setDialogState(prev => ({ ...prev, isOpen: false }));
  }, [dialogState.resolver]);

  const handleCancel = useCallback(() => {
    if (dialogState.resolver) {
      dialogState.resolver(false);
    }
    setDialogState(prev => ({ ...prev, isOpen: false }));
  }, [dialogState.resolver]);

  const confirmDialog = {
    isOpen: dialogState.isOpen,
    onClose: handleCancel,
    onConfirm: handleConfirm,
    title: dialogState.title,
    message: dialogState.message,
    confirmText: dialogState.confirmText,
    cancelText: dialogState.cancelText,
    variant: dialogState.variant
  };

  return {
    confirmDialog,
    showConfirm
  };
}
